// src/AppContent.js
import React, { useEffect, useState } from 'react';
import { Routes, Route, Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  BottomNavigation,
  BottomNavigationAction,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import SoupKitchenIcon from '@mui/icons-material/SoupKitchen';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CurrencyRubleIcon from '@mui/icons-material/CurrencyRuble';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import Profile from './pages/Settings/Profile';
import Dashboard from './pages/Events/Dashboard';
import MetricsPage from './pages/Metrics/MetricsPage';
import CostPage from './pages/Cost/CostPage';
import LoginPage from './pages/Auth/LoginPage';
import RegisterPage from './pages/Auth/RegisterPage';
import { handleTelegramAuth, verifyToken, isTokenExpired, refreshSession } from './services/authService';
import {
  AUTH_EVENT_LOGOUT,
  clearBrowserSession,
  hasSessionHint,
  subscribeAuthBroadcast,
} from './services/sessionStore';
import { fetchActiveServer } from './services/serverService';
import { useGlobalGroupSelector } from './hooks/useGlobalGroupSelector';
import { getTelegramInitData, isTelegramMiniApp } from './utils/platform';
import AppLayout, { MOBILE_NAV_HEIGHT } from './components/layout/AppLayout';
import UpdateBanner from './components/UpdateBanner';
import PushBridge from './components/notifications/PushBridge';
import NotificationsPage from './pages/Notifications/NotificationsPage';
import KitchenPage from './pages/Kitchen/KitchenPage';
import { useIikoCloudStatus } from './hooks/useIikoCloudStatus';
import { NEUTRAL_ACTIVE_TAB, SECTION_ACCENTS, motionSx, navGradientLineSx, navSurfaceSx } from './styles/tokens';
import { useSlidingIndicator } from './components/motion/useSlidingIndicator';

// Уведомления на телефоне — колокольчиком в строке выбора ресторана (FiltersTopRow).
// «Кухня» — только у серверов с подключением iikoCloud
const MOBILE_NAV_ITEMS = [
  { to: '/metrics', label: 'Выручка', Icon: CurrencyRubleIcon },
  { to: '/cost', label: 'Себест.', Icon: ReceiptLongIcon },
  { to: '/kitchen', label: 'Кухня', Icon: SoupKitchenIcon, iikocloud: true },
  { to: '/dashboard', label: 'События', Icon: DashboardIcon },
  { to: '/profile', label: 'Настройки', Icon: SettingsIcon },
];

const PILL_WIDTH = 56;
const PILL_HEIGHT = 28;
const LABEL_GAP = 4; // mt: 0.5 у подписи

// Конечное место пилюли у активного пункта. Иконку MUI сдвигает вверх переходом padding-top,
// поэтому её текущую позицию не меряем: содержимое (пилюля + подпись) центрируется в пункте
function measurePill(node, containerRect) {
  const rect = node.getBoundingClientRect();
  const label = node.querySelector('.MuiBottomNavigationAction-label');
  const labelHeight = label ? label.offsetHeight + LABEL_GAP : 0;
  return {
    x: rect.left - containerRect.left + (rect.width - PILL_WIDTH) / 2,
    y: rect.top - containerRect.top + (rect.height - PILL_HEIGHT - labelHeight) / 2,
    width: PILL_WIDTH,
  };
}

// Адрес, с которым открыли приложение, — до любых переходов при старте.
// Нажатие на push открывает /notifications?id=…: туда и нужно попасть, а не на «Выручку»
const DEEP_LINK_PATHS = ['/notifications'];
const initialDeepLink = DEEP_LINK_PATHS.includes(window.location.pathname)
  ? `${window.location.pathname}${window.location.search}`
  : null;
let deepLinkConsumed = false;

export default function AppContent({ buildVersion }) {
  const [userData, setUserData] = useState(null);
  const [currentTab, setCurrentTab] = useState('/profile');
  const [authError, setAuthError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const navigate = useNavigate();
  const location = useLocation();

  // Глобальный выбор ресторана
  const { selectedGroup, setSelectedGroup, allGroups, activeServerName } = useGlobalGroupSelector();
  // Подключение iikoCloud у активного сервера: пункт «Кухня» и доступ к /kitchen
  const iikocloud = useIikoCloudStatus();
  const mobileNavItems = MOBILE_NAV_ITEMS.filter((item) => !item.iikocloud || iikocloud);
  // «Пилюля» под иконкой активного раздела переезжает между пунктами; у «Настроек» её нет
  const pillKey = SECTION_ACCENTS[location.pathname] && mobileNavItems.some((item) => item.to === location.pathname)
    ? location.pathname
    : null;
  const pill = useSlidingIndicator(pillKey, measurePill);

  // 🔹 Обновляем currentTab при изменении пути
  useEffect(() => {
    if (location.pathname) {
      setCurrentTab(location.pathname);
    }
  }, [location.pathname]);


  // --- Telegram WebApp управление экраном и safe-area ---
  useEffect(() => {
    if (!isTelegramMiniApp()) {
      document.documentElement.style.setProperty('--safe-area-top', 'env(safe-area-inset-top, 0px)');
      return;
    }

    const tg = window.Telegram?.WebApp;

    if (tg) {
      tg.ready();
      tg.expand(); // Разворачиваем приложение на весь экран
      tg.disableVerticalSwipes(); // Запрет свайпа вниз для сворачивания
      tg.enableClosingConfirmation(); // Показывать подтверждение перед закрытием
      
      // Перехватываем изменение viewport и предотвращаем закрытие
      const handleViewportChanged = (event) => {
        // Если viewport изменился (приложение пытается закрыться), возвращаем обратно
        if (event.isStateStable === false) {
          tg.expand(); // Принудительно разворачиваем обратно
        }
      };
      
      // Подписываемся на события изменения viewport
      if (tg.onEvent) {
        tg.onEvent('viewportChanged', handleViewportChanged);
      }
      
      // Предотвращаем начало жеста свайпа вниз на верхней части экрана
      let touchStartY = 0;
      const handleTouchStart = (e) => {
        touchStartY = e.touches[0].clientY;
      };
      
      const handleTouchMove = (e) => {
        const touchY = e.touches[0].clientY;
        const deltaY = touchY - touchStartY;
        
        // Если свайп вниз начинается в верхней части экрана (первые 100px), предотвращаем его
        if (touchStartY < 100 && deltaY > 0 && Math.abs(deltaY) > 10) {
          e.preventDefault();
          e.stopPropagation();
        }
      };
      
      document.addEventListener('touchstart', handleTouchStart, { passive: false });
      document.addEventListener('touchmove', handleTouchMove, { passive: false });

      // 🔹 устанавливаем CSS-переменную для верхнего safe-area
      const safeTop = tg?.safeAreaInsetTop || 0;
      document.documentElement.style.setProperty('--safe-area-top', `${safeTop}px`);
      
      // Cleanup при размонтировании
      return () => {
        if (tg.offEvent) {
          tg.offEvent('viewportChanged', handleViewportChanged);
        }
        document.removeEventListener('touchstart', handleTouchStart);
        document.removeEventListener('touchmove', handleTouchMove);
      };
    } else {
      document.documentElement.style.setProperty('--safe-area-top', '20px');
    }
  }, []);

  const openApp = (nextUser, token) => {
    setUserData(nextUser);
    setAuthError(false);

    // Открыли по ссылке из push — остаёмся на ней (один раз, при первом входе)
    if (initialDeepLink && !deepLinkConsumed) {
      deepLinkConsumed = true;
      setCurrentTab(initialDeepLink.split('?')[0]);
      navigate(initialDeepLink, { replace: true });
      // Активный сервер всё равно обновляем (localStorage для разделов), но без перехода и уведомления
      return fetchActiveServer(token).catch(() => null);
    }

    return (async () => {
      console.log('🔹 Запрос активного сервера...');
      let activeServer = null;
      try {
        activeServer = await fetchActiveServer(token);
      } catch (err) {
        console.warn('⚠️ Ошибка при запросе активного сервера:', err?.message || err);
      }

      if (activeServer) {
        console.log('✅ Активный сервер найден → переход на /metrics');
        setCurrentTab('/metrics');
        navigate('/metrics', { replace: true }); // обычный запуск — без уведомления
      } else {
        console.log('⚠️ Активный сервер отсутствует → переход на /profile');
        setCurrentTab('/profile');
        navigate('/profile', { replace: true });
        setSnackbar({
          open: true,
          message: 'Активный сервер не найден — добавьте сервер в профиле',
          severity: 'warning',
        });
      }
    })();
  };

  const enterBrowserApp = async () => {
    const token = localStorage.getItem('token');
    const nextUser = token ? await verifyToken(token) : null;
    if (!nextUser) {
      navigate('/login', { replace: true });
      return;
    }
    setUserData(nextUser);
    setAuthError(false);
    setLoading(false);
    setCurrentTab('/profile');
    navigate('/profile', { replace: true });
  };

  // --- Авторизация и инициализация приложения ---
  useEffect(() => {
    const initAuth = async () => {
      try {
        console.log('🔹 Старт initAuth');

        if (isTelegramMiniApp()) {
          const tgWebAppData = getTelegramInitData();
          let token = null;

          if (tgWebAppData) {
            console.log('🔹 Авторизация через Telegram...');
            token = await handleTelegramAuth(tgWebAppData);
          } else {
            token = localStorage.getItem('token');
            console.log('🔹 Токен из localStorage:', token ? 'найден' : 'отсутствует');
            if (token && isTokenExpired(token)) {
              console.log('⚠️ Токен истёк');
              localStorage.removeItem('token');
              token = null;
            }
          }

          if (!token) {
            console.log('❌ Токен отсутствует — авторизация не выполнена');
            setAuthError(true);
            setLoading(false);
            return;
          }

          console.log('🔹 Проверка токена через verifyToken...');
          const nextUser = await verifyToken(token);
          console.log('✅ Ответ verifyToken:', nextUser);

          if (!nextUser) {
            localStorage.removeItem('token');
            setAuthError(true);
            setLoading(false);
            return;
          }

          sessionStorage.removeItem('tg-auth-reloaded');
          await openApp(nextUser, token);
          setLoading(false);
          return;
        }

        // Без флажка сессии и без access-токена refresh-cookie нет — не спрашиваем сервер зря
        const hadSession = hasSessionHint();
        let token = localStorage.getItem('token');
        let nextUser = token ? await verifyToken(token) : null;
        if (!nextUser && hadSession) {
          try {
            token = await refreshSession();
            nextUser = await verifyToken(token);
          } catch (err) {
            console.warn('Сессия браузера не восстановлена:', err?.message || err);
            nextUser = null;
          }
          if (!nextUser) clearBrowserSession();
        }

        if (!nextUser) {
          setUserData(null);
          setAuthError(false);
          setLoading(false);
          const path = window.location.pathname;
          if (path !== '/login' && path !== '/register') {
            navigate('/login', {
              replace: true,
              state: hadSession ? { message: 'Войдите снова' } : undefined,
            });
          }
          return;
        }

        await openApp(nextUser, token);
        setLoading(false);
      } catch (err) {
        console.error('❌ Ошибка в initAuth:', err);
        if (isTelegramMiniApp()) {
          setAuthError(true);
        } else {
          setAuthError(false);
          navigate('/login', { replace: true, state: { message: 'Войдите снова' } });
        }
        setLoading(false);
      }
    };

    initAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Другие вкладки и установленная PWA: выход в одной — выход во всех.
  // «refreshed» обрабатывать не нужно: токен в общем localStorage, запросы читают его каждый раз.
  useEffect(() => {
    if (isTelegramMiniApp()) return undefined;
    return subscribeAuthBroadcast((type) => {
      if (type !== AUTH_EVENT_LOGOUT) return;
      clearBrowserSession();
      if (window.location.pathname !== '/login') window.location.replace('/login');
    });
  }, []);

  useEffect(() => {
    const onTelegramAuthFailed = () => setAuthError(true);
    window.addEventListener('telegram-auth-failed', onTelegramAuthFailed);
    return () => window.removeEventListener('telegram-auth-failed', onTelegramAuthFailed);
  }, []);

  // --- UI состояния ---
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  const browserMode = !isTelegramMiniApp();

  if (authError) {
    if (browserMode) {
      return <Navigate to="/login" replace />;
    }
    return (
      <Typography variant="h6" color="error" align="center" sx={{ marginTop: '20%' }}>
        Ошибка авторизации. Попробуйте перезапустить приложение через Telegram.
      </Typography>
    );
  }

  if (browserMode && !userData) {
    return (
      <>
        <Routes>
          <Route path="/login" element={<LoginPage onSuccess={enterBrowserApp} />} />
          <Route path="/register" element={<RegisterPage onSuccess={enterBrowserApp} />} />
          <Route path="*" element={<Navigate to="/login" replace state={{ message: 'Войдите снова' }} />} />
        </Routes>
        <UpdateBanner />
      </>
    );
  }

  // --- Основной UI ---
  return (
    <>
      {/* Плашка новой версии — над нижней навигацией */}
      <UpdateBanner aboveMobileNav />
      {/* Сообщения service worker: push пришёл, клик по push, смена подписки */}
      <PushBridge />

      {/* Snackbar уведомление */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <AppLayout>
        <Routes>
          <Route path="/profile" element={<Profile isAdmin={userData?.is_admin} email={userData?.email || ''} username={userData?.username || ''} emailVerified={userData?.email_verified} createdAt={userData?.created_at} buildVersion={buildVersion} />} />
          <Route path="/dashboard" element={<Dashboard selectedGroup={selectedGroup} allGroups={allGroups} activeServerName={activeServerName} setSelectedGroup={setSelectedGroup} />} />
          <Route path="/metrics" element={<MetricsPage selectedGroup={selectedGroup} setSelectedGroup={setSelectedGroup} allGroups={allGroups} activeServerName={activeServerName} />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          {/* Вкладка «Склад» (остатки iikoCloud) — только при подключённом iikoCloud */}
          <Route path="/cost" element={<CostPage selectedGroup={selectedGroup} allGroups={allGroups} activeServerName={activeServerName} setSelectedGroup={setSelectedGroup} iikocloud={iikocloud} />} />
          {/* Пока признак неизвестен (null) — страница ждёт; без iikoCloud — на «Выручку» */}
          <Route
            path="/kitchen"
            element={
              iikocloud === false ? (
                <Navigate to="/metrics" replace />
              ) : (
                <KitchenPage selectedGroup={selectedGroup} allGroups={allGroups} activeServerName={activeServerName} setSelectedGroup={setSelectedGroup} available={iikocloud === true} />
              )
            }
          />
          <Route path="*" element={<Navigate to={currentTab} />} />
        </Routes>
      </AppLayout>

      {/* Нижняя панель навигации только на экранах уже md */}
      <Box
        sx={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 10,
          display: { xs: 'block', md: 'none' },
          pb: 'env(safe-area-inset-bottom, 0px)',
          ...navSurfaceSx(!browserMode),
          // Градиентная линия по верхнему краю вместо границы
          '&::before': navGradientLineSx('top'),
        }}
        ref={pill.containerRef}
      >
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: PILL_WIDTH,
            height: PILL_HEIGHT,
            borderRadius: `${PILL_HEIGHT / 2}px`,
            pointerEvents: 'none',
            backgroundColor: pill.position ? `rgba(${SECTION_ACCENTS[pill.position.key].rgb},0.14)` : 'transparent',
            opacity: pill.position && !pill.position.hidden ? 1 : 0,
            transform: pill.position ? `translate(${pill.position.x}px, ${pill.position.y}px)` : 'none',
            ...(pill.animate && motionSx(['transform', 'background-color', 'opacity'])),
          }}
        />
        <BottomNavigation
          value={currentTab}
          onChange={(event, newValue) => {
            setCurrentTab(newValue);
          }}
          showLabels={false}
          sx={{ position: 'relative', height: MOBILE_NAV_HEIGHT, backgroundColor: 'transparent' }}
        >
          {/* Порядок: Выручка, Себест., Кухня (с iikoCloud), События, Настройки; подпись — только у активного */}
          {mobileNavItems.map(({ to, label, Icon }) => {
            const active = location.pathname === to;
            const section = SECTION_ACCENTS[to];
            const activeColor = section ? section.tab : NEUTRAL_ACTIVE_TAB.color;

            return (
              <BottomNavigationAction
                key={to}
                ref={pill.itemRef(to)}
                component={Link}
                to={to}
                value={to}
                label={label}
                aria-label={label}
                icon={
                  // Место под иконку размером с «пилюлю»; сама пилюля — общая, над панелью (см. выше)
                  <Box
                    sx={{
                      width: PILL_WIDTH,
                      height: PILL_HEIGHT,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon sx={{ fontSize: 24, color: active ? activeColor : 'rgba(255,255,255,0.5)' }} />
                  </Box>
                }
                sx={{
                  minWidth: 0,
                  minHeight: 44,
                  color: 'rgba(255,255,255,0.5)',
                  '&.Mui-selected': { color: activeColor },
                  '& .MuiBottomNavigationAction-label, & .MuiBottomNavigationAction-label.Mui-selected': {
                    fontSize: 12,
                    fontWeight: 600,
                    mt: 0.5,
                  },
                }}
              />
            );
          })}
        </BottomNavigation>
      </Box>
    </>
  );
}
